import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Sun, Umbrella, Armchair, Tent, Info, ArrowLeft, MapPin, Users, ChevronLeft, ChevronRight, Mail, Phone } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { format, addDays, startOfDay, isSameDay, parseISO } from 'date-fns';
import { useProperty } from '@/contexts/PropertyContext';
import { ExistingReservationBanner } from '@/components/ExistingReservationBanner';
import { getGuestSession, clearGuestSession, guestHeaders, isGuestPassExpired } from '@/lib/guestSession';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';


const isBookable = (item) =>
  item.availability_status === 'available' && (!item.is_free || item.can_book_free);

const getGuestIdentity = () => {
  const validation = JSON.parse(localStorage.getItem('guestValidation') || '{}');
  const d = validation.data || {};
  if (validation.type === 'resident') {
    return { confirmation_number: null, room_number: d.unit_number || null, last_name: d.last_name || null };
  }
  const nameParts = (d.guest_name || '').trim().split(/\s+/);
  return {
    confirmation_number: d.confirmation_number || null,
    room_number: d.room_number || null,
    last_name: d.last_name || (nameParts.length ? nameParts[nameParts.length - 1] : null) || null
  };
};

// Horizontal Date Bar Component
function DateBar({ selectedDate, onDateSelect, today }) {
  const scrollRef = useRef(null);
  
  // Generate 30 days starting from the default date
  const dates = Array.from({ length: 30 }, (_, i) => addDays(today, i));
  
  const scrollLeft = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: -200, behavior: 'smooth' });
    }
  };
  
  const scrollRight = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: 200, behavior: 'smooth' });
    }
  };

  return (
    <div className="relative flex items-center gap-2 bg-white border border-[#E7E5E4] rounded-sm p-2" data-testid="date-bar">
      <button
        onClick={scrollLeft}
        className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-[#FAFAF9] hover:bg-[#F5F5F4] text-[#78716C]"
      >
        <ChevronLeft className="w-5 h-5" />
      </button>
      
      <div
        ref={scrollRef}
        className="flex gap-2 overflow-x-auto scrollbar-hide scroll-smooth"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {dates.map((date) => {
          const isSelected = isSameDay(date, selectedDate);
          const isToday = isSameDay(date, today);
          
          return (
            <button
              key={date.toISOString()}
              onClick={() => onDateSelect(date)}
              className={`flex-shrink-0 flex flex-col items-center justify-center w-16 h-16 rounded-sm transition-all ${
                isSelected
                  ? 'bg-[#9F1239] text-white shadow-lg'
                  : isToday
                  ? 'bg-[#FEF2F2] text-[#9F1239] border border-[#9F1239]'
                  : 'bg-[#FAFAF9] text-[#1C1917] hover:bg-[#F5F5F4]'
              }`}
              data-testid={`date-${format(date, 'yyyy-MM-dd')}`}
            >
              <span className={`text-xs font-medium ${isSelected ? 'text-white/80' : 'text-[#78716C]'}`}>
                {format(date, 'EEE')}
              </span>
              <span className="text-lg font-serif font-bold">
                {format(date, 'd')}
              </span>
              <span className={`text-xs ${isSelected ? 'text-white/80' : 'text-[#A8A29E]'}`}>
                {format(date, 'MMM')}
              </span>
            </button>
          );
        })}
      </div>
      
      <button
        onClick={scrollRight}
        className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-[#FAFAF9] hover:bg-[#F5F5F4] text-[#78716C]"
      >
        <ChevronRight className="w-5 h-5" />
      </button>
    </div>
  );
}

export default function InteractiveMap() {
  const navigate = useNavigate();
  const { propertyId, loading: propertyLoading, error: propertyError, refetch } = useProperty();
  const [detailedAreas, setDetailedAreas] = useState([]);
  const [selectedArea, setSelectedArea] = useState(null);
  const [maps, setMaps] = useState([]);
  const [currentMap, setCurrentMap] = useState(null);
  const [inventory, setInventory] = useState([]);
  const [selectedDate, setSelectedDate] = useState(startOfDay(new Date()));
  const [bookingWindow, setBookingWindow] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingAreas, setLoadingAreas] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [hoveredItem, setHoveredItem] = useState(null);
  const [viewMode, setViewMode] = useState('map');
  const [mapDimensions, setMapDimensions] = useState({ width: 0, height: 0 });
  const [dynamicPrices, setDynamicPrices] = useState({});
  const [loadingPrices, setLoadingPrices] = useState(false);
  const [showAreaSelection, setShowAreaSelection] = useState(true);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [bookingEmail, setBookingEmail] = useState('');
  const [bookingPhone, setBookingPhone] = useState('');
  const [smsConsent, setSmsConsent] = useState(false);
  const [isSubmittingBooking, setIsSubmittingBooking] = useState(false);
  const [existingReservation, setExistingReservation] = useState(null);
  const mapImageRef = useRef(null);

  const fetchExistingReservation = async () => {
    if (!propertyId) return;
    try {
      const response = await axios.post(`${API_URL}/reservations/guest-lookup`, {
        property_id: propertyId,
        date: format(selectedDate, 'yyyy-MM-dd')
      }, { headers: guestHeaders() });
      setExistingReservation(response.data.reservation);
    } catch (error) {
      setExistingReservation(null);
      if (isGuestPassExpired(error)) endSession('Your verification has expired');
    }
  };

  useEffect(() => {
    fetchExistingReservation();
  }, [propertyId, selectedDate]);

  const cancelExistingReservation = async () => {
    await axios.post(`${API_URL}/reservations/${existingReservation.id}/guest-cancel`, getGuestIdentity());
    setExistingReservation(null);
    setSelectedItem(null);
    if (selectedArea) await fetchAvailability();
  };

  const handleCancelExisting = async () => {
    if (!window.confirm(`Cancel your reservation for seat ${existingReservation.inventory_label}?`)) return;
    try {
      await cancelExistingReservation();
      toast.success('Reservation cancelled', { description: 'You can now book a different seat.' });
    } catch (error) {
      toast.error('Could not cancel reservation', { description: error.response?.data?.detail || 'Please try again' });
    }
  };

  const handleChangeExisting = async () => {
    if (!window.confirm(`To change, your current seat ${existingReservation.inventory_label} will be released. Continue?`)) return;
    try {
      await cancelExistingReservation();
      toast.success('Seat released', { description: 'Please select a new seat.' });
    } catch (error) {
      toast.error('Could not change reservation', { description: error.response?.data?.detail || 'Please try again' });
    }
  };

  const session = getGuestSession();

  const loadBookingWindow = async (setDefault) => {
    try {
      const res = await axios.get(`${API_URL}/properties/${propertyId}/booking-window`);
      setBookingWindow(res.data);
      if (setDefault) setSelectedDate(parseISO(res.data.default_date));
    } catch (error) {
      setBookingWindow(null);
    }
  };

  useEffect(() => {
    if (propertyId) loadBookingWindow(true);
  }, [propertyId]);

  const endSession = (message) => {
    clearGuestSession();
    if (message) toast.error(message, { description: 'Please verify again to continue booking.' });
    navigate('/check-eligibility', { replace: true });
  };

  useEffect(() => {
    if (!session) return;
    const ms = new Date(session.expiresAt) - new Date();
    const timer = setTimeout(() => endSession('Your verification has expired'), ms);
    return () => clearTimeout(timer);
  }, [session?.expiresAt]);

  // Fetch areas when propertyId is available, or fetch property directly as fallback
  useEffect(() => {
    const loadAreas = async () => {
      let pid = propertyId;
      
      // If no propertyId from context, fetch it directly as fallback
      if (!pid && !propertyLoading) {
        console.log('PropertyId not available from context, fetching directly...');
        try {
          const propResponse = await axios.get(`${API_URL}/properties`);
          if (propResponse.data && propResponse.data.length > 0) {
            // Find Acqualina Resort specifically
            const acqualina = propResponse.data.find(p => 
              p.name && p.name.toLowerCase().includes('acqualina')
            );
            pid = acqualina ? acqualina.id : propResponse.data[0].id;
            console.log('Fetched propertyId directly:', pid);
          }
        } catch (err) {
          console.error('Failed to fetch property directly:', err);
        }
      }
      
      if (pid) {
        await fetchDetailedAreasWithId(pid);
      }
    };
    
    if (!propertyLoading) {
      loadAreas();
    }
  }, [propertyId, propertyLoading]);

  // Function that accepts propertyId as parameter
  const fetchDetailedAreasWithId = async (pid) => {
    if (!pid) {
      console.log('fetchDetailedAreas: No propertyId, skipping');
      return;
    }
    console.log('fetchDetailedAreas: Fetching for propertyId:', pid);
    setLoadingAreas(true);
    try {
      const response = await axios.get(`${API_URL}/areas/detailed?property_id=${pid}`);
      console.log('fetchDetailedAreas: Response:', response.data);
      setDetailedAreas(response.data);
    } catch (error) {
      console.error('fetchDetailedAreas: Error:', error);
      toast.error('Failed to load areas. Please refresh the page.');
    } finally {
      setLoadingAreas(false);
    }
  };

  // Keep original function for compatibility
  const fetchDetailedAreas = async () => {
    await fetchDetailedAreasWithId(propertyId);
  };

  useEffect(() => {
    if (selectedArea && !showAreaSelection) {
      const loadAreaData = async () => {
        setCurrentMap(null);
        setMaps([]);
        setInventory([]);
        setDynamicPrices({});
        setSelectedItem(null);
        setMapDimensions({ width: 0, height: 0 });
        await fetchMaps();
        await fetchAvailability();
      };
      loadAreaData();
    }
  }, [selectedArea, showAreaSelection]);

  useEffect(() => {
    if (selectedArea && selectedDate && !showAreaSelection) {
      fetchAvailability();
    }
  }, [selectedDate]);

  useEffect(() => {
    if (inventory.length > 0 && selectedDate) {
      fetchDynamicPrices();
    }
  }, [inventory, selectedDate]);

  const fetchMaps = async () => {
    try {
      const response = await axios.get(`${API_URL}/maps?area_id=${selectedArea}`);
      if (response.data.length > 0) {
        setMaps(response.data);
        setCurrentMap(response.data[0]);
      } else {
        setMaps([]);
        setCurrentMap(null);
        setViewMode('grid');
      }
    } catch (error) {
      toast.error('Failed to load maps');
    }
  };

  const handleMapLoad = () => {
    if (mapImageRef.current) {
      setMapDimensions({
        width: mapImageRef.current.offsetWidth,
        height: mapImageRef.current.offsetHeight
      });
    }
  };

  const fetchAvailability = async () => {
    if (!selectedArea) return;
    setLoading(true);
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const response = await axios.get(`${API_URL}/availability?area_id=${selectedArea}&date=${dateStr}`);
      setInventory(response.data);
    } catch (error) {
      toast.error('Failed to load availability');
    } finally {
      setLoading(false);
    }
  };

  const fetchDynamicPrices = async () => {
    setLoadingPrices(true);
    const dateStr = format(selectedDate, 'yyyy-MM-dd');
    const prices = {};
    
    try {
      await Promise.all(
        inventory.map(async (item) => {
          try {
            const response = await axios.get(
              `${API_URL}/calculate-price?inventory_item_id=${item.id}&date=${dateStr}&property_id=${propertyId}`
            );
            prices[item.id] = response.data;
          } catch (err) {
            prices[item.id] = { price: item.base_price, is_free: item.is_free };
          }
        })
      );
      setDynamicPrices(prices);
    } catch (error) {
      console.error('Error fetching dynamic prices:', error);
    } finally {
      setLoadingPrices(false);
    }
  };

  const getItemPrice = (item) => {
    const priceData = dynamicPrices[item.id];
    if (priceData) {
      if (priceData.is_free) return 'FREE';
      return `$${Number(priceData.price).toFixed(2)}`;
    }
    return item.is_free ? 'FREE' : `$${Number(item.base_price).toFixed(2)}`;
  };

  const getItemPriceValue = (item) => {
    const priceData = dynamicPrices[item.id];
    if (priceData) return priceData.price;
    return item.base_price;
  };

  const handleAreaSelect = (areaId) => {
    setSelectedArea(areaId);
    setShowAreaSelection(false);
  };

  const handleBackToAreas = () => {
    setShowAreaSelection(true);
    setSelectedArea(null);
    setInventory([]);
    setSelectedItem(null);
  };

  const handleItemSelect = (item) => {
    // Check if item is available
    if (item.availability_status === 'blackout') {
      toast.error('This area is closed on this date', {
        description: item.blackout_reason || 'Please select a different date'
      });
      return;
    }
    
    if (item.availability_status !== 'available') {
      toast.error('This seat is already reserved for this date');
      return;
    }

    if (item.is_free && !item.can_book_free) {
      toast.error('This free seat cannot be booked for this date', {
        description: item.free_booking_reason || 'Please select a different date'
      });
      return;
    }

    if (existingReservation) {
      toast.error('You already have a reservation for this date', {
        description: 'Change or cancel your existing reservation first.'
      });
      return;
    }

    setSelectedItem(item);
  };

  // Open booking modal when Book Now is clicked
  const handleBookNowClick = () => {
    if (!selectedItem) return;
    
    // Double-check free item restriction
    if (selectedItem.is_free && !selectedItem.can_book_free) {
      toast.error('Free items can only be booked for today');
      return;
    }
    
    // Pre-fill email from validation data if available
    const validation = JSON.parse(localStorage.getItem('guestValidation') || '{}');
    if (validation.data?.email) {
      setBookingEmail(validation.data.email);
    }
    if (validation.data?.phone) {
      setBookingPhone(validation.data.phone);
    }
    
    setShowBookingModal(true);
  };

  const handleConfirmBooking = async () => {
    // Validate at least one contact method
    if (!bookingEmail && !bookingPhone) {
      toast.error('Please provide either an email address or phone number');
      return;
    }
    
    // Basic email validation
    if (bookingEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(bookingEmail)) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    // Basic phone validation (at least 10 digits)
    if (bookingPhone && !/^\+?[\d\s-()]{10,}$/.test(bookingPhone)) {
      toast.error('Please enter a valid phone number');
      return;
    }
    
    setIsSubmittingBooking(true);
    
    const validation = JSON.parse(localStorage.getItem('guestValidation'));
    const guestData = validation.data;
    const identity = getGuestIdentity();

    try {
      const reservationData = {
        property_id: propertyId,
        area_id: selectedArea,
        inventory_item_id: selectedItem.id,
        guest_name: validation.type === 'hotel_guest' ? guestData.guest_name : `${guestData.first_name} ${guestData.last_name}`,
        guest_email: bookingEmail || null,
        guest_phone: bookingPhone || null,
        guest_type: validation.type,
        room_number: identity.room_number,
        guest_last_name: identity.last_name,
        hotel_confirmation_code: identity.confirmation_number,
        reservation_date: format(selectedDate, 'yyyy-MM-dd'),
        adults: 2,
        adult_confirmation: true,
        validation_data: guestData,
        send_confirmation_email: !!bookingEmail,
        sms_consent: smsConsent && !!bookingPhone
      };

      const response = await axios.post(`${API_URL}/reservations`, reservationData, { headers: guestHeaders() });
      
      setShowBookingModal(false);
      toast.success('Reservation Created!', {
        description: `Your confirmation code is: ${response.data.confirmation_code}`
      });
      navigate(`/confirmation/${response.data.confirmation_code}`);
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (isGuestPassExpired(error)) {
        endSession('Your verification has expired');
      } else if (detail?.code === 'guest_has_reservation') {
        setShowBookingModal(false);
        setSelectedItem(null);
        setExistingReservation(detail.reservation);
        toast.error('You already have a reservation for this date', { description: detail.message });
      } else if (detail?.code === 'seat_just_booked') {
        setShowBookingModal(false);
        setSelectedItem(null);
        fetchAvailability();
        toast.error('This seat was just booked', { description: 'Please pick another seat.' });
      } else {
        if (error.response?.status === 409) fetchAvailability();
        toast.error('Booking Failed', {
          description: typeof detail === 'string' ? detail : detail?.message || 'Please try again'
        });
      }
    } finally {
      setIsSubmittingBooking(false);
    }
  };

  // Keep for backward compatibility but redirect to modal
  const handleBooking = () => {
    handleBookNowClick();
  };

  const getItemColor = (item) => {
    if (selectedItem?.id === item.id) return '#9F1239';
    if (existingReservation?.inventory_item_id === item.id) return '#0E7490';
    if (item.availability_status === 'blackout') return '#6B7280';
    if (item.availability_status === 'held') return '#F59E0B';
    if (item.availability_status !== 'available') return '#EF4444';
    if (item.is_free && !item.can_book_free) return '#9CA3AF';
    return '#10B981';
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'cabana': return <Tent className="w-4 h-4" />;
      case 'umbrella': return <Umbrella className="w-4 h-4" />;
      case 'sun_chair': 
      case 'lounge_chair': return <Armchair className="w-4 h-4" />;
      default: return <Sun className="w-4 h-4" />;
    }
  };

  const handleDateSelect = (date) => {
    if (date) {
      setSelectedDate(date);
      setSelectedItem(null);
    }
  };

  if (!session) {
    return <Navigate to="/check-eligibility" replace />;
  }

  // Show loading while property is being fetched
  if (propertyLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAF9]">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-[#9F1239] mx-auto" />
          <p className="mt-4 text-lg font-serif text-[#9F1239]">Loading property...</p>
        </div>
      </div>
    );
  }

  // Show error if property failed to load
  if (propertyError || !propertyId) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAF9]">
        <div className="text-center">
          <MapPin className="w-16 h-16 text-[#9F1239] mx-auto mb-4" />
          <p className="text-lg font-serif text-[#9F1239] mb-4">Unable to load property</p>
          <Button 
            onClick={refetch}
            className="bg-[#9F1239] hover:bg-[#881337]"
          >
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      {/* Header */}
      <header className="bg-white border-b border-[#E7E5E4] shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            {!showAreaSelection && (
              <Button
                variant="ghost"
                onClick={handleBackToAreas}
                className="text-[#9F1239] hover:text-[#881337] -ml-2"
                data-testid="btn-back-to-areas"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Back
              </Button>
            )}
            <span className="font-serif tracking-wide whitespace-nowrap text-lg md:text-xl text-[#1C1917]" data-testid="header-logo">Acqualina Resort</span>
            <h1 className="text-lg md:text-xl font-serif text-[#1C1917]" data-testid="page-title">
              {showAreaSelection ? 'Select Your Area' : 'Select Your Seat'}
            </h1>
          </div>
          <Button 
            variant="ghost" 
            onClick={() => endSession()}
            className="text-[#9F1239] hover:text-[#881337] text-sm"
            data-testid="btn-guest-sign-out"
          >
            Not you? Sign out
          </Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Date Bar - Always visible at top */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-serif text-lg text-[#1C1917]">Select Date</h2>
            <div className="flex items-center gap-2 text-sm text-[#78716C]">
              <Info className="w-4 h-4 text-[#9F1239]" />
              <span>Prices shown for {format(selectedDate, 'MMM d, yyyy')}</span>
              {loadingPrices && <Loader2 className="w-4 h-4 animate-spin text-[#9F1239]" />}
            </div>
          </div>
          <DateBar
            selectedDate={selectedDate}
            onDateSelect={handleDateSelect}
            today={bookingWindow ? parseISO(bookingWindow.today) : startOfDay(new Date())}
          />
          {bookingWindow && (
            <p className="text-xs text-[#78716C] mt-2" data-testid="resort-time-label">
              Resort time: {bookingWindow.now_label}
            </p>
          )}
        </div>

        {/* Area Selection View */}
        {showAreaSelection ? (
          <div className="space-y-6">
            {/* Areas Grid */}
            {loadingAreas ? (
              <div className="flex justify-center py-24">
                <div className="text-center">
                  <Loader2 className="w-12 h-12 animate-spin text-[#9F1239] mx-auto" />
                  <p className="mt-4 text-[#78716C]">Loading areas...</p>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="areas-grid">
                {detailedAreas.map((area) => (
                  <Card
                    key={area.id}
                    onClick={() => handleAreaSelect(area.id)}
                    className="bg-white border-[#E7E5E4] shadow-sm hover:shadow-lg hover:border-[#9F1239] transition-all duration-300 cursor-pointer overflow-hidden group"
                    data-testid={`area-card-${area.id}`}
                  >
                    {/* Area Image */}
                    <div className="relative h-48 overflow-hidden">
                      {area.image_url ? (
                        <img
                          src={area.image_url}
                          alt={area.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-[#9F1239] to-[#881337] flex items-center justify-center">
                          <MapPin className="w-16 h-16 text-white/30" />
                        </div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                      <div className="absolute bottom-4 left-4">
                        <span className="px-3 py-1.5 bg-white/95 backdrop-blur-sm rounded-sm text-[#9F1239] font-serif font-bold text-lg shadow-lg">
                          {area.price_display}
                        </span>
                      </div>
                      <div className="absolute top-4 right-4">
                        <span className={`px-2 py-1 rounded-sm text-xs font-medium ${
                          area.total_items > 0 ? 'bg-green-500 text-white' : 'bg-gray-500 text-white'
                        }`}>
                          {area.total_items} {area.total_items === 1 ? 'spot' : 'spots'}
                        </span>
                      </div>
                    </div>

                    <CardContent className="p-5">
                      <h3 className="text-xl font-serif font-medium text-[#1C1917] mb-2 group-hover:text-[#9F1239] transition-colors">
                        {area.name}
                      </h3>
                      
                      {area.description && (
                        <p className="text-sm text-[#78716C] mb-4 line-clamp-2">
                          {area.description}
                        </p>
                      )}

                      <div className="flex items-center justify-between pt-3 border-t border-[#E7E5E4]">
                        <div className="flex items-center gap-4 text-sm">
                          <span className="flex items-center gap-1 text-[#78716C]">
                            <Users className="w-4 h-4" />
                            {area.total_items} seats
                          </span>
                          {area.free_items > 0 && (
                            <span className="text-green-600 font-medium">
                              {area.free_items} FREE
                            </span>
                          )}
                        </div>
                        {area.avg_price > 0 && (
                          <span className="text-xs text-[#A8A29E]">
                            Avg: ${Number(area.avg_price).toFixed(2)}
                          </span>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {detailedAreas.length === 0 && !loadingAreas && (
              <div className="text-center py-16">
                <MapPin className="w-16 h-16 text-[#A8A29E] mx-auto mb-4" />
                <p className="text-[#78716C] text-lg">No areas available for this property.</p>
              </div>
            )}
          </div>
        ) : (
          /* Inventory Selection View */
          <div className="space-y-6">
            {/* Area Header */}
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-serif text-[#1C1917]">
                  {detailedAreas.find(a => a.id === selectedArea)?.name}
                </h2>
                <p className="text-sm text-[#78716C]">
                  Click on any seat to select. Prices shown for {format(selectedDate, 'MMM d, yyyy')}.
                </p>
              </div>
              
              {/* View Toggle */}
              {currentMap && (
                <div className="flex bg-white border border-[#E7E5E4] rounded-sm overflow-hidden">
                  <Button
                    onClick={() => setViewMode('map')}
                    variant="ghost"
                    size="sm"
                    className={`rounded-none ${viewMode === 'map' ? 'bg-[#9F1239] text-white hover:bg-[#881337]' : 'text-[#78716C]'}`}
                    data-testid="btn-map-view"
                  >
                    Map
                  </Button>
                  <Button
                    onClick={() => setViewMode('grid')}
                    variant="ghost"
                    size="sm"
                    className={`rounded-none ${viewMode === 'grid' ? 'bg-[#9F1239] text-white hover:bg-[#881337]' : 'text-[#78716C]'}`}
                    data-testid="btn-grid-view"
                  >
                    Grid
                  </Button>
                </div>
              )}
            </div>

            {existingReservation && (
              <ExistingReservationBanner
                reservation={existingReservation}
                onChange={handleChangeExisting}
                onCancel={handleCancelExisting}
              />
            )}

            {/* Legend */}
            <div className="flex items-center gap-6 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-green-500"></div>
                <span className="text-[#78716C]">Available</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-red-500"></div>
                <span className="text-[#78716C]">Reserved</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                <span className="text-[#78716C]">On Hold</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-[#9F1239]"></div>
                <span className="text-[#78716C]">Selected</span>
              </div>
              {existingReservation && (
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-[#0E7490]"></div>
                  <span className="text-[#78716C]">Your seat</span>
                </div>
              )}
            </div>

            {loading ? (
              <div className="flex justify-center py-24">
                <div className="text-center">
                  <Loader2 className="w-12 h-12 animate-spin text-[#9F1239] mx-auto" />
                  <p className="mt-4 text-[#78716C]">Loading availability...</p>
                </div>
              </div>
            ) : (
              <>
                {/* Map View */}
                {viewMode === 'map' && currentMap && (
                  <Card key={`map-${selectedArea}`} className="bg-white border-[#E7E5E4] shadow-sm mb-24" data-testid="map-view-card">
                    <CardContent className="p-4 pb-20">
                      <div className="relative inline-block w-full">
                        <img
                          ref={mapImageRef}
                          src={currentMap.file_url}
                          alt="Area Map"
                          className="w-full rounded-sm"
                          style={{ maxWidth: '100%', height: 'auto' }}
                          onLoad={handleMapLoad}
                        />
                        {mapDimensions.width > 0 && inventory.map((item) => {
                          const leftPercent = (item.map_x / currentMap.width) * 100;
                          const topPercent = (item.map_y / currentMap.height) * 100;
                          
                          return (
                            <div
                              key={item.id}
                              onClick={() => handleItemSelect(item)}
                              onMouseEnter={() => setHoveredItem(item)}
                              onMouseLeave={() => setHoveredItem(null)}
                              style={{
                                position: 'absolute',
                                left: `${leftPercent}%`,
                                top: `${topPercent}%`,
                                transform: 'translate(-50%, -50%)',
                                cursor: isBookable(item) ? 'pointer' : 'not-allowed',
                                zIndex: hoveredItem?.id === item.id ? 20 : 10,
                              }}
                              data-testid={`map-marker-${item.label}`}
                            >
                              <div
                                className="w-9 h-9 rounded-full border-2 border-white shadow-lg transition-all duration-200 hover:scale-125 flex items-center justify-center"
                                style={{ backgroundColor: getItemColor(item) }}
                              >
                                <span className="text-white text-xs font-bold">{item.label}</span>
                              </div>
                              {hoveredItem?.id === item.id && (
                                <div className="absolute left-1/2 -translate-x-1/2 top-full mt-2 z-30 bg-white border-2 border-[#9F1239] rounded-sm shadow-2xl p-3 whitespace-nowrap min-w-[160px]">
                                  <div className="flex items-center gap-2 mb-1">
                                    {getTypeIcon(item.type)}
                                    <p className="font-serif font-bold text-[#9F1239]">{item.label}</p>
                                  </div>
                                  <p className="text-xs text-[#78716C] capitalize">{item.type.replace(/_/g, ' ')}</p>
                                  <p className="text-xs text-[#78716C]">Capacity: {item.capacity}</p>
                                  <div className="border-t border-[#E7E5E4] pt-2 mt-2">
                                    <p className="text-lg font-serif font-bold text-[#9F1239]">
                                      {getItemPrice(item)}
                                    </p>
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Grid View */}
                {(viewMode === 'grid' || !currentMap) && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="grid-view">
                    {inventory.map((item) => (
                      <Card
                        key={item.id}
                        onClick={() => handleItemSelect(item)}
                        className={`cursor-pointer transition-all duration-300 ${
                          selectedItem?.id === item.id
                            ? 'border-2 border-[#9F1239] shadow-lg bg-[#FEF2F2]'
                            : 'border-[#E7E5E4] hover:border-[#9F1239] hover:shadow-md bg-white'
                        } ${!isBookable(item) ? 'opacity-50 cursor-not-allowed' : ''}`}
                        data-testid={`inventory-item-${item.label}`}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center gap-3">
                              <div
                                className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm"
                                style={{ backgroundColor: getItemColor(item) }}
                              >
                                {item.label}
                              </div>
                              <div>
                                <h3 className="font-serif font-medium text-[#1C1917]">{item.preview_title || item.label}</h3>
                                {existingReservation?.inventory_item_id === item.id && (
                                  <span className="inline-block mt-1 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide rounded-full bg-[#9F1239] text-white" data-testid={`your-seat-badge-${item.label}`}>
                                    Your seat
                                  </span>
                                )}
                                <p className="text-xs text-[#78716C] capitalize">{item.type.replace(/_/g, ' ')}</p>
                              </div>
                            </div>
                          </div>
                          
                          <div className="flex items-center justify-between pt-3 border-t border-[#E7E5E4]">
                            <span className="text-xs text-[#78716C]">Capacity: {item.capacity}</span>
                            <p className="text-xl font-serif font-bold text-[#9F1239]">
                              {getItemPrice(item)}
                            </p>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}

                {inventory.length === 0 && !loading && (
                  <div className="text-center py-16">
                    <Armchair className="w-16 h-16 text-[#A8A29E] mx-auto mb-4" />
                    <p className="text-[#78716C] text-lg">No seats available in this area.</p>
                    <Button
                      onClick={handleBackToAreas}
                      className="mt-4 bg-[#9F1239] hover:bg-[#881337]"
                    >
                      Choose Different Area
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Booking Summary Bar */}
      {selectedItem && !showAreaSelection && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t-2 border-[#9F1239] shadow-2xl p-4 z-50" data-testid="booking-summary">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div
                className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold"
                style={{ backgroundColor: '#9F1239' }}
              >
                {selectedItem.label}
              </div>
              <div>
                <h3 className="text-lg font-serif font-medium text-[#1C1917]">
                  {selectedItem.preview_title || selectedItem.label}
                </h3>
                <p className="text-sm text-[#78716C]">
                  {selectedItem.type.replace(/_/g, ' ')} • {format(selectedDate, 'EEE, MMM d')}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-6">
              <div className="text-right">
                <p className="text-xs text-[#78716C]">Total</p>
                <p className="text-2xl font-serif font-bold text-[#9F1239]">{getItemPrice(selectedItem)}</p>
              </div>
              <Button
                onClick={handleBooking}
                className="bg-[#9F1239] hover:bg-[#881337] text-white rounded-none px-8 py-5 text-lg font-serif shadow-lg"
                data-testid="btn-book-now"
              >
                Book Now
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Booking Contact Modal */}
      <Dialog open={showBookingModal} onOpenChange={setShowBookingModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-[#1C1917]">Complete Your Booking</DialogTitle>
            <DialogDescription className="text-[#78716C]">
              Please provide your contact information. At least one is required.
            </DialogDescription>
          </DialogHeader>
          
          {selectedItem && (
            <div className="bg-[#FEF2F2] border border-[#9F1239]/20 rounded-sm p-4 mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#9F1239] flex items-center justify-center text-white font-bold">
                  {selectedItem.label}
                </div>
                <div>
                  <p className="font-serif font-medium text-[#1C1917]">{selectedItem.preview_title || selectedItem.label}</p>
                  <p className="text-sm text-[#78716C]">{format(selectedDate, 'EEEE, MMMM d, yyyy')}</p>
                </div>
                <div className="ml-auto text-right">
                  <p className="font-serif font-bold text-[#9F1239] text-xl">{getItemPrice(selectedItem)}</p>
                </div>
              </div>
            </div>
          )}
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="booking-email" className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-[#9F1239]" />
                Email Address
              </Label>
              <Input
                id="booking-email"
                type="email"
                placeholder="your@email.com"
                value={bookingEmail}
                onChange={(e) => setBookingEmail(e.target.value)}
                className="border-[#E7E5E4] focus:border-[#9F1239] focus:ring-[#9F1239]"
                data-testid="input-booking-email"
              />
              <p className="text-xs text-[#78716C]">We'll send your confirmation here</p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="booking-phone" className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-[#9F1239]" />
                Phone Number
              </Label>
              <Input
                id="booking-phone"
                type="tel"
                placeholder="+1 (555) 123-4567"
                value={bookingPhone}
                onChange={(e) => setBookingPhone(e.target.value)}
                className="border-[#E7E5E4] focus:border-[#9F1239] focus:ring-[#9F1239]"
                data-testid="input-booking-phone"
              />
            </div>
            
            {/* SMS Consent */}
            {bookingPhone && (
              <div className="flex items-start gap-3 p-3 bg-[#FEF2F2] border border-[#9F1239]/20 rounded-sm">
                <input
                  type="checkbox"
                  id="sms-consent"
                  checked={smsConsent}
                  onChange={(e) => setSmsConsent(e.target.checked)}
                  className="mt-1 w-4 h-4 text-[#9F1239] border-[#9F1239] rounded focus:ring-[#9F1239]"
                  data-testid="checkbox-sms-consent"
                />
                <div>
                  <Label htmlFor="sms-consent" className="text-sm font-medium text-[#1C1917] cursor-pointer">
                    Send me SMS updates
                  </Label>
                  <p className="text-xs text-[#78716C] mt-1">
                    I agree to receive reservation confirmations, reminders, and weather alerts via SMS. 
                    Message & data rates may apply. 
                    <a href="/sms-terms" target="_blank" className="text-[#9F1239] hover:underline ml-1">
                      SMS Terms
                    </a>
                  </p>
                </div>
              </div>
            )}
          </div>
          
          <div className="flex gap-3 mt-6">
            <Button
              variant="outline"
              onClick={() => setShowBookingModal(false)}
              className="flex-1"
              disabled={isSubmittingBooking}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmBooking}
              className="flex-1 bg-[#9F1239] hover:bg-[#881337]"
              disabled={isSubmittingBooking}
              data-testid="btn-confirm-booking"
            >
              {isSubmittingBooking ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                'Confirm Booking'
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
